import React, { FC, isValidElement, forwardRef } from "react";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import { default as Button } from "@narvar/nth-block-button";
import {
  ButtonData,
  FooterButtonProps,
  FooterButtonType,
  FooterProps,
} from "./types";
import { useStyles } from "./FooterStyles";
import { CommonEvent } from "@narvar/nth-kit-returns-headless";

const ButtonItem = ({
  button,
  onEvent,
}: {
  button: FooterButtonProps;
  onEvent?: CommonEvent<FooterButtonType>;
}) => {
  if (isValidElement(button)) return button;

  const buttonData = button as ButtonData;

  const handleClick = () =>
    onEvent?.({ value: buttonData.type, area: "navigation", event: "click" });

  return (
    <Button
      onClick={handleClick}
      disabled={buttonData.disabled}
      outline={buttonData.outline}
      big={buttonData.big}>
      {buttonData.label ?? buttonData.type}
    </Button>
  );
};

const ButtonRow: FC<FooterProps> = ({ buttons, onEvent }) => {
  const styles = useStyles();
  return (
    <nav className={styles.buttonWrapper}>
      {buttons?.leftButton && (
        <div className={styles.leftItem}>
          <ButtonItem onEvent={onEvent} button={buttons.leftButton} />
        </div>
      )}
      {buttons?.centerButton && (
        <div className={styles.item}>
          <ButtonItem onEvent={onEvent} button={buttons.centerButton} />
        </div>
      )}
      {buttons?.rightButton && (
        <div className={styles.rightItem}>
          <ButtonItem onEvent={onEvent} button={buttons.rightButton} />
        </div>
      )}
    </nav>
  );
};

const FooterC = forwardRef<HTMLElement, FooterProps>(
  (
    {
      children,
      buttons,
      onEvent,
      above,
      below,
      divider,
      progressiveStep,
      activeStep,
    },
    ref,
  ) => {
    const { panelcontainerNoneNone } = useTheme();

    const styles = useStyles();

    const dividerThemeStyle = {
      borderTopColor: panelcontainerNoneNone?.borderTopColor ?? "#ccc",
      borderTopWidth: panelcontainerNoneNone?.borderTopWidth ?? "1px",
      borderTopStyle: panelcontainerNoneNone?.borderTopStyle ?? "solid",
    };

    const show = !progressiveStep || activeStep;
    return show ? (
      <footer className={styles.footer} ref={ref}>
        {above}
        {divider && above && (
          <hr className={styles.divider} style={dividerThemeStyle} />
        )}
        {children || <ButtonRow {...{ buttons, onEvent }} />}
        {divider && below && (
          <hr className={styles.divider} style={dividerThemeStyle} />
        )}
        {below}
      </footer>
    ) : null;
  },
);
FooterC.displayName = "FooterC";

export default withThemeProvider(FooterC, {
  themeTokens: [
    "documentbodyNoneNone",
    "panelcontainerNoneNone",
    "buttonsPrimaryDefault",
    "buttonsPrimaryHover",
    "buttonsSecondaryDefault",
  ],
});
